

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  Divider,
  Stack,
  Container
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isBefore } from 'date-fns';

// Initial state for case history
const initialCaseHistory = {
  date: null,
  presentComplaint: '',
  
  // Ear Symptoms - flat structure
  reducedHearingRight: 'no',
  reducedHearingRightDuration: '',
  reducedHearingLeft: 'no',
  reducedHearingLeftDuration: '',
  
  earDischargeRight: 'no',
  earDischargeRightDuration: '',
  earDischargeLeft: 'no',
  earDischargeLeftDuration: '',
  
  earPainRight: 'no',
  earPainRightDuration: '',
  earPainLeft: 'no',
  earPainLeftDuration: '',
  
  blockingSensationRight: 'no',
  blockingSensationRightDuration: '',
  blockingSensationLeft: 'no',
  blockingSensationLeftDuration: '',
  
  fullnessRight: 'no',
  fullnessRightDuration: '',
  fullnessLeft: 'no',
  fullnessLeftDuration: '',
  
  tinnitusRight: 'no',
  tinnitusRightDuration: '',
  tinnitusLeft: 'no',
  tinnitusLeftDuration: '',
  
  vertigo: 'no',
  vertigoDuration: '',
  
  // Prenatal History
  prenatalComplications: '',
  prenatalConsanguinity: 'no',
  prenatalRhFactor: '',
  prenatalMiscarriage: '',
  prenatalPregnancyLength: '',
  
  // Perinatal History
  perinatalDeliveryType: '',
  perinatalComplications: '',
  
  // Postnatal History
  postnatalBirthWeight: '',
  postnatalComplications: '',
  
  // Family History
  familySiblings: '',
  familyOtherMembers: '',
  
  // Developmental History
  developmentalMotor: '',
  developmentalSpeech: '',
  developmentalBehavior: '',
  developmentalSchoolProgress: '',
  
  // Hearing Loss Presence
  hearingLossFirstDetected: '',
  hearingLossOnsetAndProgress: '',
  
  // Previous Investigations
  previousInvestigationsEnt: '',
  previousInvestigationsAudiological: '',
  
  // Audiological Rehabilitation
  audiologicalRehabUsingHearingAid: 'no',
  audiologicalRehabRightDevice: '',
  audiologicalRehabLeftDevice: '',
  audiologicalRehabRightUsageDuration: '',
  audiologicalRehabLeftUsageDuration: '',
  audiologicalRehabBenefit: '',
  audiologicalRehabOtherComments: ''
};

const CaseHistoryChild = () => {
  const [demographyData, setDemographyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('excel');
  const [caseHistory, setCaseHistory] = useState(initialCaseHistory);

  useEffect(() => {
    const data = localStorage.getItem("demography");
    if (data) {
      try {
        const parsedData = JSON.parse(data);
        setDemographyData(parsedData);
      } catch (error) {
        console.error("Error parsing demography data:", error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCaseHistory(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (field) => {
    setCaseHistory(prev => ({ 
      ...prev, 
      [field]: prev[field] === 'yes' ? 'no' : 'yes' 
    }));
  };

  const handleDateChange = (date) => {
    if (date && isBefore(new Date(), date)) {
      alert("Date cannot be in the future");
      return;
    }
    setCaseHistory(prev => ({ ...prev, date }));
  };

  const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

  const handleDownloadConfirm = async () => {
    const correctKey = "Varun@6361";

    if (password !== correctKey) {
      alert("Incorrect key! Download canceled.");
      setPassword('');
      setOpenPasswordModal(false);
      return;
    }

    setOpenPasswordModal(false);
    setLoading(true);
    setSaveStatus({
      message: `Requesting Case History Child ${downloadFormat}...`,
      severity: "info",
    });

    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            table: "child_case_history",
            formName: "Child_Case_History",
            format: downloadFormat,
            key: password
          }),
        }
      );

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setSaveStatus({
          message: `Failed: ${body.message || resp.statusText}`,
          severity: "error",
        }); 
        return;
      }

      const blob = await resp.blob();
      const disposition = resp.headers.get("content-disposition") || "";

      let filename = `Child_Case_History_${Date.now()}.${
        downloadFormat === "excel" ? "xlsx" : "pdf"
      }`;

      const match = /filename="?([^"]+)"?/.exec(disposition);
      if (match && match[1]) filename = match[1];

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSaveStatus({
        message: `Download started: ${filename}`,
        severity: "success",
      });

    } catch (err) {
      console.error("Download error:", err);
      setSaveStatus({
        message: `Error downloading file: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data to send to backend
      const dataToSend = {
        ...caseHistory,
        // Include patient information from demographyData
        patientName: demographyData?.name || '',
        patientPhone: demographyData?.phone || '',
        patientIpNumber: demographyData?.ipNumber || '',
        patientOpNumber: demographyData?.opNumber || '',
        patientAddress: demographyData?.address || '',
        referredBy: demographyData?.referredBy || ''
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/casehistory/child`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) throw new Error("Failed to save");

      const data = await response.json();
      alert("✅ Child case history saved successfully!");
      
      // Reset form after successful submission
      setCaseHistory(initialCaseHistory);
      
      console.log("Saved:", data);
    } catch (error) {
      console.error("Error:", error);
      alert("❌ Failed to save child case history");
    }
  };

  if (!demographyData) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Typography>Loading patient data...</Typography>
    </Box>
  );

  return ( 
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          {/* Header Section */}
          <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <NoteAddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight={600} color="text.primary">
                    Case History - Child
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Complete child patient assessment form
                  </Typography>
             
                </Box>
              </Stack> 
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  textTransform: 'none'
                }}
              >
                Download Form
              </Button>
            </Stack>

        

            {/* Assessment Date */}
            <Box sx={{ mb: 2 }}>
              <DatePicker
                label="Date of Assessment"
                value={caseHistory.date}
                onChange={handleDateChange}
                maxDate={new Date()}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    sx={{ width: 300 }}
                  />
                )}
              />
            </Box>
          </Paper>
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
            Patient Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Name:</strong> {demographyData.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Age:</strong> {demographyData.age}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>IP Number:</strong> {demographyData.ip_number}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>OP Number:</strong> {demographyData.op_number}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Phone:</strong> {demographyData.phone}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Address:</strong> {demographyData.address}</Typography>
            </Grid>
          </Grid>
        </Paper>
          {/* Main Form */}
          <Paper elevation={0} sx={{ p: 4, border: '1px solid #e0e0e0' }}>
            {/* Present Complaint */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                Present Complaint
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                name="presentComplaint"
                value={caseHistory.presentComplaint}
                onChange={handleChange}
                variant="outlined"
                placeholder="Describe the child's main complaints..."
              />
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 1. Ear-related signs and symptoms */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                1. Ear-related signs and symptoms
              </Typography>

              <Card variant="outlined">
                <CardContent>
                  {/* Reduced hearing sensitivity */}
                  <Box sx={{ mb: 3 }}>
                    <Typography fontWeight={600} gutterBottom>Reduced hearing sensitivity</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.reducedHearingRight === 'yes'}
                              onChange={() => handleCheckboxChange('reducedHearingRight')}
                            />
                          }
                          label="Right"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.reducedHearingRight === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Right)"
                            name="reducedHearingRightDuration"
                            value={caseHistory.reducedHearingRightDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.reducedHearingLeft === 'yes'}
                              onChange={() => handleCheckboxChange('reducedHearingLeft')}
                            />
                          }
                          label="Left"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.reducedHearingLeft === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Left)"
                            name="reducedHearingLeftDuration"
                            value={caseHistory.reducedHearingLeftDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Ear discharge */}
                  <Box sx={{ mb: 3 }}>
                    <Typography fontWeight={600} gutterBottom>Ear discharge</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.earDischargeRight === 'yes'}
                              onChange={() => handleCheckboxChange('earDischargeRight')}
                            />
                          }
                          label="Right"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.earDischargeRight === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Right)"
                            name="earDischargeRightDuration"
                            value={caseHistory.earDischargeRightDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid> 
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.earDischargeLeft === 'yes'}
                              onChange={() => handleCheckboxChange('earDischargeLeft')}
                            />
                          }
                          label="Left"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.earDischargeLeft === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Left)"
                            name="earDischargeLeftDuration"
                            value={caseHistory.earDischargeLeftDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Ear pain */}
                  <Box sx={{ mb: 3 }}>
                    <Typography fontWeight={600} gutterBottom>Ear pain</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.earPainRight === 'yes'}
                              onChange={() => handleCheckboxChange('earPainRight')}
                            />
                          }
                          label="Right"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.earPainRight === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Right)"
                            name="earPainRightDuration"
                            value={caseHistory.earPainRightDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.earPainLeft === 'yes'}
                              onChange={() => handleCheckboxChange('earPainLeft')}
                            />
                          }
                          label="Left"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.earPainLeft === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Left)"
                            name="earPainLeftDuration"
                            value={caseHistory.earPainLeftDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Blocking sensation */}
                  <Box sx={{ mb: 3 }}>
                    <Typography fontWeight={600} gutterBottom>Blocking sensation</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.blockingSensationRight === 'yes'}
                              onChange={() => handleCheckboxChange('blockingSensationRight')}
                            />
                          }
                          label="Right"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.blockingSensationRight === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Right)"
                            name="blockingSensationRightDuration"
                            value={caseHistory.blockingSensationRightDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.blockingSensationLeft === 'yes'}
                              onChange={() => handleCheckboxChange('blockingSensationLeft')}
                            />
                          }
                          label="Left"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.blockingSensationLeft === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Left)"
                            name="blockingSensationLeftDuration"
                            value={caseHistory.blockingSensationLeftDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Fullness of the ear */}
                  <Box sx={{ mb: 3 }}>
                    <Typography fontWeight={600} gutterBottom>Fullness of the ear</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.fullnessRight === 'yes'}
                              onChange={() => handleCheckboxChange('fullnessRight')}
                            />
                          }
                          label="Right"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.fullnessRight === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Right)"
                            name="fullnessRightDuration"
                            value={caseHistory.fullnessRightDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.fullnessLeft === 'yes'}
                              onChange={() => handleCheckboxChange('fullnessLeft')}
                            />
                          }
                          label="Left"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.fullnessLeft === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Left)"
                            name="fullnessLeftDuration"
                            value={caseHistory.fullnessLeftDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Tinnitus */}
                  <Box sx={{ mb: 3 }}>
                    <Typography fontWeight={600} gutterBottom>Tinnitus</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.tinnitusRight === 'yes'}
                              onChange={() => handleCheckboxChange('tinnitusRight')}
                            />
                          }
                          label="Right"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.tinnitusRight === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Right)"
                            name="tinnitusRightDuration"
                            value={caseHistory.tinnitusRightDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.tinnitusLeft === 'yes'}
                              onChange={() => handleCheckboxChange('tinnitusLeft')}
                            />
                          }
                          label="Left"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.tinnitusLeft === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration (Left)"
                            name="tinnitusLeftDuration"
                            value={caseHistory.tinnitusLeftDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Vertigo/Balancing problems */}
                  <Box>
                    <Typography fontWeight={600} gutterBottom>Vertigo/Balancing problems</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={caseHistory.vertigo === 'yes'}
                              onChange={() => handleCheckboxChange('vertigo')}
                            />
                          }
                          label="Yes"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {caseHistory.vertigo === 'yes' && (
                          <TextField
                            fullWidth
                            label="Duration"
                            name="vertigoDuration"
                            value={caseHistory.vertigoDuration}
                            onChange={handleChange}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 2. Pre-, peri-, post natal history */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                2. Pre-, peri-, post natal history
              </Typography>

              <Card variant="outlined">
                <CardContent>
                  {/* a. Pre-Natal History */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      a. Pre-Natal History
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink={true}>Complications</InputLabel>
                          <Select
                            name="prenatalComplications"
                            value={caseHistory.prenatalComplications}
                            onChange={handleChange}
                            label="Complications"
                            displayEmpty
                          >
                            <MenuItem value=""><em>Select Complications</em></MenuItem>
                            <MenuItem value="Illness">Illness</MenuItem>
                            <MenuItem value="Accident">Accident</MenuItem>
                            <MenuItem value="Medication">Medication</MenuItem>
                            <MenuItem value="None">None</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink={true}>Consanguinity</InputLabel>
                          <Select
                            name="prenatalConsanguinity"
                            value={caseHistory.prenatalConsanguinity}
                            onChange={handleChange}
                            label="Consanguinity"
                            displayEmpty
                          >
                            <MenuItem value=""><em>Select</em></MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Rh. Factor"
                          name="prenatalRhFactor"
                          value={caseHistory.prenatalRhFactor}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Miscarriage"
                          name="prenatalMiscarriage"
                          value={caseHistory.prenatalMiscarriage}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Length of Pregnancy"
                          name="prenatalPregnancyLength"
                          value={caseHistory.prenatalPregnancyLength}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* b. Perinatal History */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      b. Perinatal History
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink={true}>Type of delivery</InputLabel>
                          <Select
                            name="perinatalDeliveryType"
                            value={caseHistory.perinatalDeliveryType}
                            onChange={handleChange}
                            label="Type of delivery"
                            displayEmpty
                          >
                            <MenuItem value=""><em>Select Delivery Type</em></MenuItem>
                            <MenuItem value="Normal">Normal</MenuItem>
                            <MenuItem value="Caesarian">Caesarian</MenuItem>
                            <MenuItem value="Prolonged labour">Prolonged labour</MenuItem>
                            <MenuItem value="Forceps delivery">Forceps delivery</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink={true}>Complications</InputLabel>
                          <Select
                            name="perinatalComplications"
                            value={caseHistory.perinatalComplications}
                            onChange={handleChange}
                            label="Complications"
                            displayEmpty
                          >
                            <MenuItem value=""><em>Select Complications</em></MenuItem>
                            <MenuItem value="Jaundice">Jaundice</MenuItem>
                            <MenuItem value="Anoxia">Anoxia</MenuItem>
                            <MenuItem value="Cyanosis">Cyanosis</MenuItem>
                            <MenuItem value="Asphyxia">Asphyxia</MenuItem>
                            <MenuItem value="None">None</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* c. Post-Natal History */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      c. Post-Natal History
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Birth Weight"
                          name="postnatalBirthWeight"
                          value={caseHistory.postnatalBirthWeight}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink={true}>Complications</InputLabel>
                          <Select
                            name="postnatalComplications"
                            value={caseHistory.postnatalComplications}
                            onChange={handleChange}
                            label="Complications"
                            displayEmpty
                          >
                            <MenuItem value=""><em>Select Complications</em></MenuItem>
                            <MenuItem value="Illness">Illness</MenuItem>
                            <MenuItem value="Accident">Accident</MenuItem>
                            <MenuItem value="Ear Infections">Ear Infections</MenuItem>
                            <MenuItem value="None">None</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 3. Family history of Hearing loss */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                3. Family history of Hearing loss
              </Typography>
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Siblings"
                        name="familySiblings"
                        value={caseHistory.familySiblings}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Other family members"
                        name="familyOtherMembers"
                        value={caseHistory.familyOtherMembers}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 4. Developmental History */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                4. Developmental History
              </Typography>
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Motor Development"
                        name="developmentalMotor"
                        value={caseHistory.developmentalMotor}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Speech Development"
                        name="developmentalSpeech"
                        value={caseHistory.developmentalSpeech}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Behavior"
                        name="developmentalBehavior"
                        value={caseHistory.developmentalBehavior}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="School progress"
                        name="developmentalSchoolProgress"
                        value={caseHistory.developmentalSchoolProgress}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 5. In case of presence of hearing loss */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                5. In case of presence of hearing loss
              </Typography>
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="When was the hearing loss first detected?"
                        name="hearingLossFirstDetected"
                        value={caseHistory.hearingLossFirstDetected}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Onset and progress of hearing loss"
                        name="hearingLossOnsetAndProgress"
                        value={caseHistory.hearingLossOnsetAndProgress}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 6. Results of previous and other investigations */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                6. Results of previous and other investigations
              </Typography>
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="ENT / The Medical Office"
                        name="previousInvestigationsEnt"
                        value={caseHistory.previousInvestigationsEnt}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Previous Audiologist investigations"
                        name="previousInvestigationsAudiological"
                        value={caseHistory.previousInvestigationsAudiological}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ my: 4 }} />
            {/* 7. Audiological rehabilitation */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 4 }}>
                7. Audiological rehabilitation
              </Typography>
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel shrink={true}>Using hearing aid?</InputLabel>
                        <Select
                          name="audiologicalRehabUsingHearingAid"
                          value={caseHistory.audiologicalRehabUsingHearingAid}
                          onChange={handleChange}
                          label="Using hearing aid?"
                          displayEmpty
                        >
                          <MenuItem value=""><em>Select an option</em></MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {caseHistory.audiologicalRehabUsingHearingAid === 'yes' && (
                    <>
                      {/* Type & Make of Device */}
                      <Grid container spacing={2} alignItems="center" sx={{ mt: 3 }}>
                        <Grid item xs={12} md={3}>
                          <Typography fontWeight={600}>Type & make of the device:</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Right ear"
                            name="audiologicalRehabRightDevice"
                            value={caseHistory.audiologicalRehabRightDevice}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Left ear"
                            name="audiologicalRehabLeftDevice"
                            value={caseHistory.audiologicalRehabLeftDevice}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>

                      {/* Usage Duration */}
                      <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                        <Grid item xs={12} md={3}>
                          <Typography fontWeight={600}>Usage duration:</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Right ear"
                            name="audiologicalRehabRightUsageDuration"
                            value={caseHistory.audiologicalRehabRightUsageDuration}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Left ear"
                            name="audiologicalRehabLeftUsageDuration"
                            value={caseHistory.audiologicalRehabLeftUsageDuration}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>

                      {/* Benefit from device use */}
                      <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                        <Grid item xs={12} md={3}>
                          <Typography fontWeight={600}>Benefit from device use:</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            name="audiologicalRehabBenefit"
                            value={caseHistory.audiologicalRehabBenefit}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>

                      {/* Other comments */}
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={3}>
                          <Typography fontWeight={600}>Other comments:</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            name="audiologicalRehabOtherComments"
                            value={caseHistory.audiologicalRehabOtherComments}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSubmit} 
                size="large"
                sx={{ 
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  textTransform: 'none'
                }}
              >
                Submit Case History
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Download Modal */}
        <Dialog 
          open={openPasswordModal} 
          onClose={handlePasswordModalClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Download Data</Typography>
            <IconButton
              aria-label="close"
              onClick={handlePasswordModalClose}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Enter Security Key"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="outlined"
                sx={{ mb: 3 }}
                helperText="Enter the security key to download data"
              />
              <FormControl fullWidth>
                <InputLabel>Download Format</InputLabel>
                <Select
                  value={downloadFormat}
                  onChange={(e) => setDownloadFormat(e.target.value)}
                  label="Download Format"
                >
                  <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                  <MenuItem value="pdf">PDF (.pdf)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePasswordModalClose} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleDownloadConfirm} 
              variant="contained" 
              color="primary"
              disabled={!password}
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default CaseHistoryChild;